import type {
  ApplianceCreateInput,
  ApplianceUpdateInput,
  DocumentRegisterInput,
  DocumentSignedUrlInput,
  DocumentUploadInput,
  LeaseCreateInput,
  LeaseDocumentUploadInput,
  LeaseTenantAddInput,
  LeaseUpdateInput,
  LoginInput,
  MessagePostInput,
  PostEntryInput,
  MagicLinkRequestInput,
  MagicLinkVerifyInput,
  MfaConfirmInput,
  MfaVerifyInput,
  MoveOutDeductionCreateInput,
  MoveOutDeductionUpdateInput,
  MoveOutDocumentAttachInput,
  MoveOutDocumentUpdateInput,
  MoveOutRecipientAddInput,
  MoveOutRecipientUpdateInput,
  MoveOutReportCreateInput,
  MoveOutReportUpdateInput,
  MoveOutReopenInput,
  MoveOutStatementVisibilityInput,
  MoveOutRecipientDeliveryInput,
  MoveOutSendInput,
  MoveOutSignInput,
  OwnerSignatureImageInput,
  OwnerSignatureUpdateInput,
  OwnerUpdateInput,
  PasswordChangeInput,
  PayIntentInput,
  PaymentMethodCreateInput,
  PaymentMethodUpdateInput,
  PropertyCreateInput,
  PropertyUpdateInput,
  RuleKey,
  ScheduleReplaceInput,
  StaffCreateInput,
  StaffUpdateInput,
  TenantCreateInput,
  ThreadCreateInput,
  TenantUpdateInput,
  UnitCreateInput,
  UnitUpdateInput,
  ContractorUpdateInput,
  ContractorInviteInput,
  ContractorJobListQuery,
  ContractorPhotosInput,
  ContractorProfileCreateInput,
  ContractorProfileUpdateInput,
  ContractorStatusInput,
  WorkOrderCostListQuery,
  WorkOrderCostReviewInput,
  WorkOrderCostSubmitInput,
  WorkOrderCostUpdateInput,
  WorkOrderSetContractorInput,
  ContractorScheduleProposalInput,
  ScheduleProposalListQuery,
  ScheduleProposalReviewInput,
  WorkOrderManualCostInput,
  WorkOrderAdminCreateInput,
  WorkOrderAssignInput,
  WorkOrderCancelInput,
  WorkOrderCloseInput,
  WorkOrderDispatchInput,
  WorkOrderNoteInput,
  WorkOrderResolveInput,
  WorkOrderScheduleInput,
  WorkOrderSubmitInput,
  WorkOrderTransitionInput,
} from './schemas';
import type {
  ApiErrorBody,
  ApplianceDTO,
  AuthAckResponse,
  AuthConfigResponse,
  ContractorDTO,
  ContractorJobDetailResponse,
  ContractorJobListResponse,
  ContractorProfileDTO,
  ContractorProfileListResponse,
  WorkOrderCostDTO,
  WorkOrderCostListResponse,
  WorkOrderCostReviewDTO,
  ScheduleProposalDTO,
  ScheduleProposalListResponse,
  ScheduleProposalReviewDTO,
  DocumentDTO,
  DocumentSignedUrlResponse,
  HealthResponse,
  LeaseDTO,
  LedgerBalanceDTO,
  LedgerEntryDTO,
  LedgerListResponse,
  LedgerReceiptResponse,
  LoginResponse,
  MessageDTO,
  MfaConfirmResponse,
  MfaEnrollResponse,
  MoveOutFinalizeResponse,
  MoveOutDeliveryStateDTO,
  MoveOutReportDTO,
  MoveOutReportResponse,
  MoveOutSendResponse,
  MoveOutSignResponse,
  OwnerDTO,
  OwnerSignatureDTO,
  PayConfigResponse,
  PayIntentResponse,
  PaymentMethodDTO,
  PaymentMethodListResponse,
  PaymentScheduleDTO,
  PortalDocumentsResponse,
  PortalLeaseResponse,
  PortalLedgerResponse,
  PortalOverviewDTO,
  PortalPaymentMethodsResponse,
  PropertyDTO,
  RuleResponse,
  SearchResponse,
  SessionResponse,
  StaffDTO,
  StaffListResponse,
  StaffCreateResponse,
  TenantDTO,
  ThreadDetailResponse,
  ThreadListResponse,
  UnitDTO,
  WorkOrderDetailResponse,
  WorkOrderListResponse,
  WorkOrderScheduleResponse,
} from './types';
import type {
  PaymentActivitySummaryDTO,
  PaymentAttemptConfirmResultInput,
  PaymentAttemptDTO,
  PaymentAttemptListResponse,
  PaymentAttemptSyncResponse,
  StripeWebhookStatusDTO,
  TenantPaymentAttemptDTO,
  TenantPaymentAttemptListResponse,
} from './payments';
import type { AuditListResponse, AuditPersonKind, AuditPersonResponse } from './audit-log';

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: ApiErrorBody,
  ) {
    super(body?.error ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

async function parseBody(res: Response): Promise<ApiErrorBody> {
  try {
    return (await res.json()) as ApiErrorBody;
  } catch {
    return { error: `HTTP ${res.status}` };
  }
}

/**
 * A tiny typed client for the PropMgt API. `baseUrl` is empty by default so the SPA
 * calls its own origin; pass an absolute base for cross-origin dev.
 *
 * CSRF: call `setCsrfToken(token)` with the value from `GET /api/auth/session` (or a
 * login response); the client then echoes it as `X-CSRF-Token` on every mutation.
 */
export function createApiClient(baseUrl = '') {
  let csrfToken: string | null = null;

  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    // Same-origin (the Worker serves both the SPA and the API), so the default cookie
    // behaviour applies — no `credentials: 'include'`, which keeps this portable across
    // the DOM and Workers type libs.
    const headers: Record<string, string> = { Accept: 'application/json' };
    const method = (init?.method ?? 'GET').toUpperCase();
    const isMutation = method !== 'GET' && method !== 'HEAD';
    if (isMutation && csrfToken) headers['X-CSRF-Token'] = csrfToken;
    if (init?.body != null) headers['Content-Type'] = 'application/json';
    if (init?.headers) Object.assign(headers, init.headers as Record<string, string>);
    const res = await fetch(baseUrl + path, { ...init, headers });
    if (!res.ok) {
      throw new ApiError(res.status, await parseBody(res));
    }
    return (await res.json()) as T;
  }

  function jsonBody(data: unknown, method = 'POST'): RequestInit {
    return { method, body: JSON.stringify(data) };
  }

  const auth = {
    login: async (input: LoginInput) => {
      const res = await request<LoginResponse>('/api/auth/login', jsonBody(input));
      csrfToken = res.csrfToken;
      return res;
    },
    magicLink: (input: MagicLinkRequestInput) =>
      request<AuthAckResponse>('/api/auth/magic-link', jsonBody(input)),
    verifyMagicLink: async (input: MagicLinkVerifyInput) => {
      const res = await request<SessionResponse>('/api/auth/magic-link/verify', jsonBody(input));
      csrfToken = res.csrfToken;
      return res;
    },
    session: async () => {
      const res = await request<SessionResponse>('/api/auth/session');
      csrfToken = res.csrfToken;
      return res;
    },
    logout: () => request<AuthAckResponse>('/api/auth/logout', { method: 'POST' }),
    changePassword: (input: PasswordChangeInput) =>
      request<AuthAckResponse>('/api/auth/password/change', jsonBody(input)),
    mfaEnroll: () => request<MfaEnrollResponse>('/api/auth/mfa/enroll', { method: 'POST' }),
    mfaConfirm: async (input: MfaConfirmInput) => {
      const res = await request<MfaConfirmResponse>('/api/auth/mfa/enroll/confirm', jsonBody(input));
      csrfToken = res.csrfToken; // session id rotated on confirm → adopt the new CSRF token
      return res;
    },
    mfaVerify: async (input: MfaVerifyInput) => {
      const res = await request<SessionResponse>('/api/auth/mfa/verify', jsonBody(input));
      csrfToken = res.csrfToken;
      return res;
    },
    /** Public feature flags for the login screen (is admin IAM SSO available?). */
    config: () => request<AuthConfigResponse>('/api/auth/config'),
    /**
     * The admin IAM SSO entry point. Return a URL to navigate to with a FULL-PAGE load (not
     * `fetch`) so the browser follows the 302 to IAM and carries the `__Host-` state cookie back
     * on the callback. `baseUrl` is honored for cross-origin dev.
     */
    oidcLoginUrl: () => `${baseUrl}/api/auth/oidc/login`,
  };

  const q = (id: string) => encodeURIComponent(id);

  /** Owner records + eligibility flags (T3.1; owner ✅, staff read-only). */
  const owners = {
    list: () => request<{ owners: OwnerDTO[] }>('/api/owners'),
    get: (id: string) => request<OwnerDTO>(`/api/owners/${q(id)}`),
    update: (id: string, input: OwnerUpdateInput) =>
      request<OwnerDTO>(`/api/owners/${q(id)}`, jsonBody(input, 'PATCH')),
  };

  /** Properties CRUD (T3.2; owner ✅ / staff ✅ no DELETE). */
  const properties = {
    list: () => request<{ properties: PropertyDTO[] }>('/api/properties'),
    get: (id: string) => request<PropertyDTO>(`/api/properties/${q(id)}`),
    create: (input: PropertyCreateInput) =>
      request<PropertyDTO>('/api/properties', jsonBody(input)),
    update: (id: string, input: PropertyUpdateInput) =>
      request<PropertyDTO>(`/api/properties/${q(id)}`, jsonBody(input, 'PUT')),
    remove: (id: string) =>
      request<AuthAckResponse>(`/api/properties/${q(id)}`, { method: 'DELETE' }),
  };

  /** Units + appliances CRUD nested under a property (T3.3). */
  const units = {
    list: (propertyId: string) =>
      request<{ units: UnitDTO[] }>(`/api/properties/${q(propertyId)}/units`),
    get: (propertyId: string, unitId: string) =>
      request<UnitDTO>(`/api/properties/${q(propertyId)}/units/${q(unitId)}`),
    create: (propertyId: string, input: UnitCreateInput) =>
      request<UnitDTO>(`/api/properties/${q(propertyId)}/units`, jsonBody(input)),
    update: (propertyId: string, unitId: string, input: UnitUpdateInput) =>
      request<UnitDTO>(`/api/properties/${q(propertyId)}/units/${q(unitId)}`, jsonBody(input, 'PUT')),
    remove: (propertyId: string, unitId: string) =>
      request<AuthAckResponse>(`/api/properties/${q(propertyId)}/units/${q(unitId)}`, {
        method: 'DELETE',
      }),
    addAppliance: (propertyId: string, unitId: string, input: ApplianceCreateInput) =>
      request<ApplianceDTO>(
        `/api/properties/${q(propertyId)}/units/${q(unitId)}/appliances`,
        jsonBody(input),
      ),
    updateAppliance: (
      propertyId: string,
      unitId: string,
      applianceId: string,
      input: ApplianceUpdateInput,
    ) =>
      request<ApplianceDTO>(
        `/api/properties/${q(propertyId)}/units/${q(unitId)}/appliances/${q(applianceId)}`,
        jsonBody(input, 'PUT'),
      ),
    removeAppliance: (propertyId: string, unitId: string, applianceId: string) =>
      request<AuthAckResponse>(
        `/api/properties/${q(propertyId)}/units/${q(unitId)}/appliances/${q(applianceId)}`,
        { method: 'DELETE' },
      ),
  };

  /** Tenants CRUD (T4.1; owner ✅ / staff ✅). */
  const tenants = {
    list: () => request<{ tenants: TenantDTO[] }>('/api/tenants'),
    get: (id: string) => request<TenantDTO>(`/api/tenants/${q(id)}`),
    create: (input: TenantCreateInput) => request<TenantDTO>('/api/tenants', jsonBody(input)),
    update: (id: string, input: TenantUpdateInput) =>
      request<TenantDTO>(`/api/tenants/${q(id)}`, jsonBody(input, 'PUT')),
    remove: (id: string) => request<AuthAckResponse>(`/api/tenants/${q(id)}`, { method: 'DELETE' }),
    /** Invite a tenant to the portal (find-or-create their magic-link login + email a sign-in link). */
    invite: (id: string) => request<AuthAckResponse>(`/api/tenants/${q(id)}/invite`, { method: 'POST' }),
  };

  /** Staff / admin account management (OWNER-ONLY). */
  const staff = {
    list: () => request<StaffListResponse>('/api/staff'),
    /** Create an owner/staff login — returns the one-time temp password to relay. */
    create: (input: StaffCreateInput) => request<StaffCreateResponse>('/api/staff', jsonBody(input)),
    /** Change role / activate | suspend. */
    update: (id: string, input: StaffUpdateInput) =>
      request<StaffDTO>(`/api/staff/${q(id)}`, jsonBody(input, 'PATCH')),
  };

  /** Leases (T4.1–T4.3; owner ✅ / staff ✅ no DELETE; tenant 📖🔒own on get). */
  const leases = {
    list: () => request<{ leases: LeaseDTO[] }>('/api/leases'),
    get: (id: string) => request<LeaseDTO>(`/api/leases/${q(id)}`),
    create: (input: LeaseCreateInput) => request<LeaseDTO>('/api/leases', jsonBody(input)),
    update: (id: string, input: LeaseUpdateInput) =>
      request<LeaseDTO>(`/api/leases/${q(id)}`, jsonBody(input, 'PUT')),
    addTenant: (id: string, input: LeaseTenantAddInput) =>
      request<LeaseDTO>(`/api/leases/${q(id)}/tenants`, jsonBody(input)),
    removeTenant: (id: string, tenantId: string) =>
      request<LeaseDTO>(`/api/leases/${q(id)}/tenants/${q(tenantId)}`, { method: 'DELETE' }),
    replaceSchedule: (id: string, input: ScheduleReplaceInput) =>
      request<PaymentScheduleDTO>(`/api/leases/${q(id)}/schedule`, jsonBody(input, 'PUT')),
    /** EPIC 7 — executed-lease PDF upload → a `documents` row + `executedDocumentId`. */
    uploadDocument: (id: string, input: LeaseDocumentUploadInput) =>
      request<LeaseDTO>(`/api/leases/${q(id)}/document`, jsonBody(input)),
  };

  /** Ledger (T5.1–T5.3; per-lease DO-backed). owner/staff post; tenant 📖🔒own reads + receipt. */
  const ledger = {
    /** POST a manual entry (owner/staff). */
    post: (leaseId: string, input: PostEntryInput) =>
      request<LedgerEntryDTO>(`/api/leases/${q(leaseId)}/ledger`, jsonBody(input)),
    /** List the lease's entries (mirror projection) + the DO-authoritative balance. */
    list: (leaseId: string) => request<LedgerListResponse>(`/api/leases/${q(leaseId)}/ledger`),
    /** The DO-authoritative running balance. */
    balance: (leaseId: string) =>
      request<LedgerBalanceDTO>(`/api/leases/${q(leaseId)}/ledger/balance`),
    /** Generate (or re-mint the download for) an entry's receipt (owner/staff + tenant-own). */
    receipt: (leaseId: string, entryId: string) =>
      request<LedgerReceiptResponse>(
        `/api/leases/${q(leaseId)}/ledger/entries/${q(entryId)}/receipt`,
        { method: 'POST' },
      ),
  };

  /** Documents (T7.1; the unified permissioned store). owner/staff manage; tenant 📖🔒own. */
  const documents = {
    list: () => request<{ documents: DocumentDTO[] }>('/api/documents'),
    get: (id: string) => request<DocumentDTO>(`/api/documents/${q(id)}`),
    upload: (input: DocumentUploadInput) => request<DocumentDTO>('/api/documents', jsonBody(input)),
    register: (input: DocumentRegisterInput) =>
      request<DocumentDTO>('/api/documents/register', jsonBody(input)),
    signedUrl: (id: string, input?: DocumentSignedUrlInput) =>
      request<DocumentSignedUrlResponse>(`/api/documents/${q(id)}/signed-url`, jsonBody(input ?? {})),
    remove: (id: string) =>
      request<AuthAckResponse>(`/api/documents/${q(id)}`, { method: 'DELETE' }),
    /** The relative content path for a minted token (GET it directly — session-exempt). */
    contentUrl: (id: string, token: string) =>
      `/api/documents/${q(id)}/content?token=${encodeURIComponent(token)}`,
  };

  /** Work orders — owner/staff queue + lifecycle (T6.1–T6.3). */
  const workOrders = {
    list: () => request<WorkOrderListResponse>('/api/work-orders'),
    get: (id: string) => request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}`),
    /** Admin-raised work order (owner/staff). */
    create: (input: WorkOrderAdminCreateInput) =>
      request<WorkOrderDetailResponse>('/api/work-orders', jsonBody(input)),
    /** Generic status transition (owner/staff) — `scheduled` is refused (use `schedule`). */
    transition: (id: string, input: WorkOrderTransitionInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}`, jsonBody(input, 'PATCH')),
    triage: (id: string, note?: string) =>
      request<WorkOrderDetailResponse>(
        `/api/work-orders/${q(id)}`,
        jsonBody({ status: 'triaged', note: note ?? null }, 'PATCH'),
      ),
    assign: (id: string, input: WorkOrderAssignInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/assign`, jsonBody(input)),
    /** Schedule entry → the §1954 24-hour notice pipeline (409 insufficient_notice if <24h). */
    /** Re-serve the §1954 notice for an already-scheduled job (idempotent; 201 new / 200 existing). */
    serveEntryNotice: (id: string) =>
      request<WorkOrderScheduleResponse>(`/api/work-orders/${q(id)}/entry-notice`, { method: 'POST' }),
    schedule: (id: string, input: WorkOrderScheduleInput) =>
      request<WorkOrderScheduleResponse>(`/api/work-orders/${q(id)}/schedule`, jsonBody(input)),
    resolve: (id: string, input: WorkOrderResolveInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/resolve`, jsonBody(input)),
    close: (id: string, input?: WorkOrderCloseInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/close`, jsonBody(input ?? {})),
    cancel: (id: string, input: WorkOrderCancelInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/cancel`, jsonBody(input)),
    /** Append a landlord-internal note (NOT a transition) — hidden from the tenant portal. */
    note: (id: string, input: WorkOrderNoteInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/note`, jsonBody(input)),
    /** Email the job to the maintenance contractor (NOT a transition). Default recipients = the
     *  org contractor list; `toEmail` overrides. 409 no_contractor_configured when none is set. */
    dispatch: (id: string, input: WorkOrderDispatchInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/dispatch`, jsonBody(input)),
    /** Set / clear the assigned contractor WITHOUT a status change (null = unassign). */
    setContractor: (id: string, input: WorkOrderSetContractorInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/contractor`, jsonBody(input, 'PUT')),
    /** Contractor-submitted costs on this work order (owner/staff). */
    costs: (id: string) => request<WorkOrderCostListResponse>(`/api/work-orders/${q(id)}/costs`),
    /** Set the manual (owner/staff) part of the job cost; the total is recomputed server-side. */
    setManualCost: (id: string, input: WorkOrderManualCostInput) =>
      request<WorkOrderDetailResponse>(`/api/work-orders/${q(id)}/manual-cost`, jsonBody(input, 'PUT')),
    /** Download the tax spreadsheet (.xlsx) of completed work + cost as a Blob (binary, not JSON). */
    exportXlsx: async (params: {
      year?: number;
      from?: string;
      to?: string;
      status?: 'resolved' | 'closed' | 'all';
    } = {}): Promise<Blob> => {
      const qs = new URLSearchParams();
      if (params.year != null) qs.set('year', String(params.year));
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.status) qs.set('status', params.status);
      const query = qs.toString();
      const res = await fetch(`${baseUrl}/api/work-orders/export.xlsx${query ? `?${query}` : ''}`, {
        headers: {
          Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      });
      if (!res.ok) throw new ApiError(res.status, await parseBody(res));
      return res.blob();
    },
  };

  /**
   * Move-out report + § 1950.5 itemized security-deposit statement (0.24.0; owner/staff + MFA).
   * Every mutation returns the full refreshed report. A FINALIZED report is locked (409
   * `report_finalized`); only the statement's tenant visibility can still change.
   */
  const moveOut = {
    /** The lease's live report, or `{ report: null }` if none has been started. */
    forLease: (leaseId: string) =>
      request<MoveOutReportResponse>(`/api/move-out?leaseId=${q(leaseId)}`),
    get: (id: string) => request<MoveOutReportDTO>(`/api/move-out/${q(id)}`),
    create: (input: MoveOutReportCreateInput) =>
      request<MoveOutReportDTO>('/api/move-out', jsonBody(input)),
    update: (id: string, input: MoveOutReportUpdateInput) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}`, jsonBody(input, 'PATCH')),
    /** Discard a DRAFT report (soft delete). */
    remove: (id: string) => request<AuthAckResponse>(`/api/move-out/${q(id)}`, { method: 'DELETE' }),
    addRecipient: (id: string, input: MoveOutRecipientAddInput) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/recipients`, jsonBody(input)),
    updateRecipient: (id: string, recipientId: string, input: MoveOutRecipientUpdateInput) =>
      request<MoveOutReportDTO>(
        `/api/move-out/${q(id)}/recipients/${q(recipientId)}`,
        jsonBody(input, 'PATCH'),
      ),
    removeRecipient: (id: string, recipientId: string) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/recipients/${q(recipientId)}`, {
        method: 'DELETE',
      }),
    addDeduction: (id: string, input: MoveOutDeductionCreateInput) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/deductions`, jsonBody(input)),
    updateDeduction: (id: string, deductionId: string, input: MoveOutDeductionUpdateInput) =>
      request<MoveOutReportDTO>(
        `/api/move-out/${q(id)}/deductions/${q(deductionId)}`,
        jsonBody(input, 'PATCH'),
      ),
    removeDeduction: (id: string, deductionId: string) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/deductions/${q(deductionId)}`, {
        method: 'DELETE',
      }),
    attachDocument: (id: string, input: MoveOutDocumentAttachInput) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/documents`, jsonBody(input)),
    updateDocument: (id: string, attachmentId: string, input: MoveOutDocumentUpdateInput) =>
      request<MoveOutReportDTO>(
        `/api/move-out/${q(id)}/documents/${q(attachmentId)}`,
        jsonBody(input, 'PATCH'),
      ),
    detachDocument: (id: string, attachmentId: string) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/documents/${q(attachmentId)}`, {
        method: 'DELETE',
      }),
    /** Draft statement preview as a PDF Blob (binary GET — nothing is stored or finalized). */
    previewPdf: async (id: string): Promise<Blob> => {
      const res = await fetch(`${baseUrl}/api/move-out/${q(id)}/preview.pdf`, {
        headers: { Accept: 'application/pdf' },
      });
      if (!res.ok) throw new ApiError(res.status, await parseBody(res));
      return res.blob();
    },
    /** Validate, generate + store the statement PDF, and lock the report. */
    finalize: (id: string) =>
      request<MoveOutFinalizeResponse>(`/api/move-out/${q(id)}/finalize`, { method: 'POST' }),
    /** Share / un-share the finalized statement document with the lease's tenants. */
    setStatementVisibility: (id: string, input: MoveOutStatementVisibilityInput) =>
      request<MoveOutReportDTO>(
        `/api/move-out/${q(id)}/statement-visibility`,
        jsonBody(input, 'PATCH'),
      ),
    /**
     * 0.28.1 (OWNER) — reopen a FINALIZED report for amendment: back to draft as the next revision;
     * the current statement (and any signed copy) stays on file as a superseded revision.
     */
    reopen: (id: string, input: MoveOutReopenInput) =>
      request<MoveOutReportDTO>(`/api/move-out/${q(id)}/reopen`, jsonBody(input)),
    /** 0.27.0 (OWNER) — signing status, delivery emails, the packet, and the delivery register. */
    delivery: (id: string) => request<MoveOutDeliveryStateDTO>(`/api/move-out/${q(id)}/delivery`),
    /** 0.27.0 (OWNER) — sign the finalized statement (a separate signed copy; idempotent). */
    sign: (id: string, input: MoveOutSignInput = {}) =>
      request<MoveOutSignResponse>(`/api/move-out/${q(id)}/sign`, jsonBody(input)),
    /** 0.27.0 (OWNER) — set / clear a recipient's delivery email (allowed after finalize). */
    setRecipientDeliveryEmail: (id: string, recipientId: string, input: MoveOutRecipientDeliveryInput) =>
      request<MoveOutDeliveryStateDTO>(
        `/api/move-out/${q(id)}/recipients/${q(recipientId)}/delivery`,
        jsonBody(input, 'PATCH'),
      ),
    /** 0.27.0 (OWNER) — email the signed statement, one email per recipient. */
    send: (id: string, input: MoveOutSendInput) =>
      request<MoveOutSendResponse>(`/api/move-out/${q(id)}/send`, jsonBody(input)),
  };

  /** Organization settings — the maintenance contractor (EPIC 6 dispatch target). */
  const org = {
    getContractor: () => request<ContractorDTO>('/api/org/contractor'),
    updateContractor: (input: ContractorUpdateInput) =>
      request<ContractorDTO>('/api/org/contractor', jsonBody(input, 'PUT')),
    /** 0.27.0 (OWNER) — the calling owner's signature profile (typed name/title + image). */
    getSignature: () => request<OwnerSignatureDTO>('/api/org/signature'),
    updateSignature: (input: OwnerSignatureUpdateInput) =>
      request<OwnerSignatureDTO>('/api/org/signature', jsonBody(input, 'PUT')),
    /** Upload / replace the handwritten signature image (PNG / JPEG, ≤ 1 MB). */
    uploadSignatureImage: (input: OwnerSignatureImageInput) =>
      request<OwnerSignatureDTO>('/api/org/signature/image', jsonBody(input)),
  };

  /** Contractor directory (0.25.0; owner/staff + MFA). Invite sends a 24h magic link. */
  const contractors = {
    list: () => request<ContractorProfileListResponse>('/api/contractors'),
    get: (id: string) => request<ContractorProfileDTO>(`/api/contractors/${q(id)}`),
    create: (input: ContractorProfileCreateInput) =>
      request<ContractorProfileDTO>('/api/contractors', jsonBody(input)),
    update: (id: string, input: ContractorProfileUpdateInput) =>
      request<ContractorProfileDTO>(`/api/contractors/${q(id)}`, jsonBody(input, 'PATCH')),
    invite: (id: string, input?: ContractorInviteInput) =>
      request<ContractorProfileDTO>(`/api/contractors/${q(id)}/invite`, jsonBody(input ?? {})),
    /** Deactivate: suspends the login + revokes every session at once. */
    deactivate: (id: string) =>
      request<ContractorProfileDTO>(`/api/contractors/${q(id)}/deactivate`, { method: 'POST' }),
    activate: (id: string) =>
      request<ContractorProfileDTO>(`/api/contractors/${q(id)}/activate`, { method: 'POST' }),
  };

  /** Contractor-submitted cost review (0.25.0). List = owner/staff; approve/reject = OWNER + MFA. */
  const workOrderCosts = {
    list: (query: Partial<WorkOrderCostListQuery> = {}) =>
      request<WorkOrderCostListResponse>(
        `/api/work-order-costs${query.status ? `?status=${encodeURIComponent(query.status)}` : ''}`,
      ),
    /** `expectedAmountCents` = the amount on screen; 409 `cost_changed` if it was edited since. */
    approve: (id: string, input: WorkOrderCostReviewInput) =>
      request<WorkOrderCostReviewDTO>(`/api/work-order-costs/${q(id)}/approve`, jsonBody(input)),
    reject: (id: string, input: WorkOrderCostReviewInput) =>
      request<WorkOrderCostReviewDTO>(`/api/work-order-costs/${q(id)}/reject`, jsonBody(input)),
  };

  /** Contractor schedule proposals (0.25.0). List = owner/staff; confirm/decline = OWNER + MFA. */
  const scheduleProposals = {
    list: (query: Partial<ScheduleProposalListQuery> = {}) => {
      const qs = new URLSearchParams();
      if (query.status) qs.set('status', query.status);
      if (query.workOrderId) qs.set('workOrderId', query.workOrderId);
      const s = qs.toString();
      return request<ScheduleProposalListResponse>(`/api/schedule-proposals${s ? `?${s}` : ''}`);
    },
    /** Serves the §1954 entry notice + schedules the job (409 proposal_too_late if <24h now). */
    confirm: (id: string, input?: ScheduleProposalReviewInput) =>
      request<ScheduleProposalReviewDTO>(`/api/schedule-proposals/${q(id)}/confirm`, jsonBody(input ?? {})),
    decline: (id: string, input?: ScheduleProposalReviewInput) =>
      request<ScheduleProposalReviewDTO>(`/api/schedule-proposals/${q(id)}/decline`, jsonBody(input ?? {})),
  };

  /**
   * The contractor's own job app (0.25.0; contractor role only). Every call is scoped server-side
   * to jobs assigned to the caller's contractor profile (anything else → 404).
   */
  const contractor = {
    profile: () => request<ContractorProfileDTO>('/api/contractor/profile'),
    jobs: (query: Partial<ContractorJobListQuery> = {}) =>
      request<ContractorJobListResponse>(
        `/api/contractor/work-orders${query.status ? `?status=${encodeURIComponent(query.status)}` : ''}`,
      ),
    job: (id: string) => request<ContractorJobDetailResponse>(`/api/contractor/work-orders/${q(id)}`),
    setStatus: (id: string, input: ContractorStatusInput) =>
      request<ContractorJobDetailResponse>(`/api/contractor/work-orders/${q(id)}/status`, jsonBody(input)),
    note: (id: string, input: WorkOrderNoteInput) =>
      request<ContractorJobDetailResponse>(`/api/contractor/work-orders/${q(id)}/note`, jsonBody(input)),
    /** Propose an entry window — the OWNER confirms it (only then is the tenant notified). */
    proposeSchedule: (id: string, input: ContractorScheduleProposalInput) =>
      request<ScheduleProposalDTO>(`/api/contractor/work-orders/${q(id)}/schedule-proposals`, jsonBody(input)),
    proposals: (id: string) =>
      request<{ proposals: ScheduleProposalDTO[] }>(`/api/contractor/work-orders/${q(id)}/schedule-proposals`),
    withdrawProposal: (proposalId: string) =>
      request<AuthAckResponse>(`/api/contractor/schedule-proposals/${q(proposalId)}`, { method: 'DELETE' }),
    addPhotos: (id: string, input: ContractorPhotosInput) =>
      request<ContractorJobDetailResponse>(`/api/contractor/work-orders/${q(id)}/photos`, jsonBody(input)),
    costs: (id: string) =>
      request<{ costs: WorkOrderCostDTO[] }>(`/api/contractor/work-orders/${q(id)}/costs`),
    submitCost: (id: string, input: WorkOrderCostSubmitInput) =>
      request<WorkOrderCostDTO>(`/api/contractor/work-orders/${q(id)}/costs`, jsonBody(input)),
    updateCost: (costId: string, input: WorkOrderCostUpdateInput) =>
      request<WorkOrderCostDTO>(`/api/contractor/costs/${q(costId)}`, jsonBody(input, 'PATCH')),
    withdrawCost: (costId: string) =>
      request<AuthAckResponse>(`/api/contractor/costs/${q(costId)}`, { method: 'DELETE' }),
    /** Mint a short-lived download token for a document attached to one of the caller's jobs. */
    documentUrl: (id: string, documentId: string) =>
      request<DocumentSignedUrlResponse>(
        `/api/contractor/work-orders/${q(id)}/documents/${q(documentId)}/signed-url`,
        { method: 'POST' },
      ),
  };

  /**
   * Messaging (T8.1). In-app threads + messages scoped to a lease/unit, with an email fan-out on
   * post. Access is participant-derived: owner/staff see the whole org, a tenant only their own
   * threads. `search` is org-scoped + post-filtered to accessible threads.
   */
  const messaging = {
    /** List threads (owner/staff org-wide; tenant participant-only). */
    listThreads: () => request<ThreadListResponse>('/api/threads'),
    /** Create a thread per lease/unit (participants derived server-side; optional opening body). */
    createThread: (input: ThreadCreateInput) =>
      request<ThreadDetailResponse>('/api/threads', jsonBody(input)),
    /** A thread + its messages. */
    getThread: (id: string) => request<ThreadDetailResponse>(`/api/threads/${q(id)}`),
    /** Just the messages of a thread. */
    listMessages: (id: string) =>
      request<{ messages: MessageDTO[] }>(`/api/threads/${q(id)}/messages`),
    /** Post a message (participant-only) → email fan-out enqueue. */
    postMessage: (id: string, input: MessagePostInput) =>
      request<MessageDTO>(`/api/threads/${q(id)}/messages`, jsonBody(input)),
    /** Mark the thread read (updates the caller's last_read_at). */
    markRead: (id: string) =>
      request<{ ok: true; lastReadAt: string }>(`/api/threads/${q(id)}/read`, { method: 'POST' }),
    /** Full-text search over accessible message bodies. */
    search: (query: string) =>
      request<SearchResponse>(`/api/messages/search?q=${encodeURIComponent(query)}`),
  };

  /**
   * Accepted payment methods (T9.1 / §1947.3). owner/staff manage the org-wide catalog; the server
   * invariant refuses any change leaving the enabled set without a fee-free non-EFT path (422
   * `non_eft_path_required`). Tenants read the enabled set via `portal.paymentMethods` below.
   */
  const paymentMethods = {
    list: () => request<PaymentMethodListResponse>('/api/payment-methods'),
    create: (input: PaymentMethodCreateInput) =>
      request<PaymentMethodDTO>('/api/payment-methods', jsonBody(input)),
    update: (id: string, input: PaymentMethodUpdateInput) =>
      request<PaymentMethodDTO>(`/api/payment-methods/${q(id)}`, jsonBody(input, 'PUT')),
    remove: (id: string) =>
      request<AuthAckResponse>(`/api/payment-methods/${q(id)}`, { method: 'DELETE' }),
  };

  /**
   * 0.26.0 — payment activity (owner/staff; sync/cancel/stripe-status are OWNER-only + MFA). Every
   * step of an online ACH payment, mirrored from Stripe — see /api/payment-attempts.
   */
  const paymentAttempts = {
    list: (filter: { leaseId?: string; tenantId?: string } = {}) => {
      const params = new URLSearchParams();
      if (filter.leaseId) params.set('leaseId', filter.leaseId);
      if (filter.tenantId) params.set('tenantId', filter.tenantId);
      const qs = params.toString();
      return request<PaymentAttemptListResponse>(`/api/payment-attempts${qs ? `?${qs}` : ''}`);
    },
    summary: () => request<PaymentActivitySummaryDTO>('/api/payment-attempts/summary'),
    get: (id: string) => request<PaymentAttemptDTO>(`/api/payment-attempts/${q(id)}`),
    cancel: (id: string) => request<PaymentAttemptDTO>(`/api/payment-attempts/${q(id)}/cancel`, { method: 'POST' }),
    sync: () => request<PaymentAttemptSyncResponse>('/api/payment-attempts/sync', { method: 'POST' }),
    stripeStatus: () => request<StripeWebhookStatusDTO>('/api/payment-attempts/stripe-status'),
  };

  /**
   * 0.26.0 — the audit log (OWNER + MFA only). Filters: actorUserId, subject=<tenant|staff|contractor|
   * lease>:<id>, action prefix, from/to (yyyy-mm-dd), cursor pagination (newest first).
   */
  type AuditFilterInput = { actorUserId?: string; subject?: string; action?: string; from?: string; to?: string };
  const auditQs = (f: AuditFilterInput & { cursor?: string | null; limit?: number }): string => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    return s ? `?${s}` : '';
  };
  const xlsxBlob = async (path: string): Promise<Blob> => {
    const res = await fetch(`${baseUrl}${path}`, {
      headers: { Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    });
    if (!res.ok) throw new ApiError(res.status, await parseBody(res));
    return res.blob();
  };
  const audit = {
    list: (f: AuditFilterInput & { cursor?: string | null; limit?: number } = {}) =>
      request<AuditListResponse>(`/api/audit${auditQs(f)}`),
    exportXlsx: (f: AuditFilterInput = {}) => xlsxBlob(`/api/audit/export.xlsx${auditQs(f)}`),
    person: (kind: AuditPersonKind, id: string, opts: { cursor?: string | null; limit?: number } = {}) =>
      request<AuditPersonResponse>(`/api/audit/people/${q(kind)}/${q(id)}${auditQs(opts)}`),
    personExportXlsx: (kind: AuditPersonKind, id: string) =>
      xlsxBlob(`/api/audit/people/${q(kind)}/${q(id)}/export.xlsx`),
  };

  /**
   * Tenant portal — the tenant's own scoped read aggregates (T9.1; §iii 🔒own, all requireRole
   * tenant + tenant-scoped server-side). A tenant with no active lease gets a valid empty shape.
   */
  const portal = {
    /** Dashboard aggregate: active lease summary + balance + next installments + counters. */
    overview: () => request<PortalOverviewDTO>('/api/portal/overview'),
    /** The tenant's active lease (full LeaseDTO with the slim co-tenant projection) or null. */
    lease: () => request<PortalLeaseResponse>('/api/portal/lease'),
    /** The tenant's ledger entries + DO-authoritative balance + installment schedule. */
    ledger: () => request<PortalLedgerResponse>('/api/portal/ledger'),
    /** The tenant's own tenant-visible documents (download via a minted signed-url). */
    documents: () => request<PortalDocumentsResponse>('/api/portal/documents'),
    /** The ENABLED accepted payment methods incl. the mandatory §1947.3 non-EFT path + copy. */
    paymentMethods: () =>
      request<PortalPaymentMethodsResponse>('/api/portal/payment-methods'),
    /** The tenant's own message threads (participant-derived). */
    messages: () => request<ThreadListResponse>('/api/portal/messages'),
    /** Online ACH rent payment (one-time). `config` gates the UI; `intent` opens a PaymentIntent. */
    pay: {
      config: () => request<PayConfigResponse>('/api/portal/pay/config'),
      intent: (input: PayIntentInput) =>
        request<PayIntentResponse>('/api/portal/pay/intent', jsonBody(input)),
    },
    /** 0.26.0 — the tenant's OWN payment attempts (every step, plain language). */
    paymentAttempts: {
      list: () => request<TenantPaymentAttemptListResponse>('/api/portal/payment-attempts'),
      /** Report the Stripe.js confirm result (advisory; the webhook is authoritative). */
      confirmResult: (id: string, input: PaymentAttemptConfirmResultInput) =>
        request<TenantPaymentAttemptDTO>(`/api/portal/payment-attempts/${q(id)}/confirm-result`, jsonBody(input)),
      /** Continue an unfinished payment (returns the PaymentIntent client secret). */
      resume: (id: string) => request<PayIntentResponse>(`/api/portal/payment-attempts/${q(id)}/resume`, { method: 'POST' }),
      cancel: (id: string) => request<TenantPaymentAttemptDTO>(`/api/portal/payment-attempts/${q(id)}/cancel`, { method: 'POST' }),
    },
    workOrders: {
      /** Tenant submits a work order for their OWN unit/lease (photos → R2). */
      submit: (input: WorkOrderSubmitInput) =>
        request<WorkOrderDetailResponse>('/api/portal/work-orders', jsonBody(input)),
      /** The tenant's own work orders. */
      list: () => request<WorkOrderListResponse>('/api/portal/work-orders'),
      get: (id: string) =>
        request<WorkOrderDetailResponse>(`/api/portal/work-orders/${q(id)}`),
    },
  };

  return {
    /** Manually set the CSRF token echoed on mutations (also set by login/session). */
    setCsrfToken: (token: string | null) => {
      csrfToken = token;
    },
    health: () => request<HealthResponse>('/health'),
    getRule: (key: RuleKey, asOf?: string) =>
      request<RuleResponse>(
        `/api/rules/${encodeURIComponent(key)}${asOf ? `?asOf=${encodeURIComponent(asOf)}` : ''}`,
      ),
    auth,
    owners,
    properties,
    units,
    tenants,
    staff,
    leases,
    ledger,
    documents,
    workOrders,
    org,
    contractors,
    workOrderCosts,
    scheduleProposals,
    contractor,
    moveOut,
    messaging,
    paymentMethods,
    paymentAttempts,
    audit,
    portal,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
